import { Highlight, themes as prismThemes } from "prism-react-renderer";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Code2, Eye } from "lucide-react";
import { useThemeStore } from "../../../../stores/theme";
import { isPreviewable, useArtifactStore, viewOf } from "../../../../stores/artifact";
import { useChatStore } from "../../../../stores/chat";
import { BranchNav } from "../../../chat/BranchNav";
import type { DockKindProps } from "../../registry";
import { artifactData } from "./data";
import { useArtifactVersions } from "./useArtifactVersions";
import styles from "./ArtifactPanel.module.css";

/**
 * The artifact panel body (2026-07-15) — extracted from ArtifactCanvas when the
 * canvas folded into the dock. It renders ONLY the document plus the controls
 * that describe how you're looking at it; the card's chrome, tabs, resizing and
 * close all belong to the dock host now.
 *
 * Source↔Preview and the version switcher ride in a header row INSIDE the body
 * rather than in the card's chrome. Two reasons, and the second is the real one:
 * they are statements about the document, not actions on the panel — and the
 * chrome row already carries the tab strip, so a segmented control plus a
 * ‹n/m› switcher would crowd the tabs down to slivers in a 340px column. The
 * reference's Browser card does the same thing with its URL bar.
 */

const REMARK_PLUGINS = [remarkGfm];

// react-markdown (v9, no rehype-raw) ESCAPES raw HTML to visible text, so an
// HTML comment — pure plumbing in every markdown dialect, hidden by every
// renderer that treats it as HTML — showed up as literal <!-- ... --> noise
// (owner report 2026-08-12, the media derive markers). Comments are not
// content; strip them from the PREVIEW only. Source view keeps the true
// bytes — that is what "Source" means.
function stripHtmlComments(md: string): string {
	return md.replace(/<!--[\s\S]*?-->/g, "");
}

// Rendered as a live document in a sandboxed iframe (scripts run in a NULL
// origin — they cannot reach the parent app, cookies or storage, the standard
// artifact sandbox). Markdown gets react-markdown instead.
const IFRAME_LANGS = new Set(["html", "htm", "svg", "xml"]);

export function ArtifactPanel({ panel }: DockKindProps) {
	const { language, content } = artifactData(panel);
	const theme = useThemeStore((s) => s.theme);
	const setView = useArtifactStore((s) => s.setView);
	const view = useArtifactStore((s) => viewOf(s, panel.id, language));
	const isStreaming = useChatStore((s) => s.isStreaming);
	const { versionCount, versionIndex, navigateVersion } = useArtifactVersions(panel);

	const previewable = isPreviewable(language);
	const showPreview = previewable && view === "preview";
	const prismTheme = theme === "light" ? prismThemes.github : prismThemes.vsDark;

	return (
		<div className={styles.panel}>
			{(previewable || versionCount > 1) && (
				<div className={styles.viewBar}>
					{previewable && (
						<div className={styles.toggle} role="tablist" aria-label="View mode">
							<button
								type="button"
								role="tab"
								aria-selected={view === "preview"}
								className={`${styles.toggleBtn} ${view === "preview" ? styles.toggleActive : ""}`}
								onClick={() => setView(panel.id, "preview")}
							>
								<Eye size={13} />
								<span>Preview</span>
							</button>
							<button
								type="button"
								role="tab"
								aria-selected={view === "source"}
								className={`${styles.toggleBtn} ${view === "source" ? styles.toggleActive : ""}`}
								onClick={() => setView(panel.id, "source")}
							>
								<Code2 size={13} />
								<span>Source</span>
							</button>
						</div>
					)}
					{versionCount > 1 && (
						<BranchNav
							currentIndex={versionIndex}
							totalSiblings={versionCount}
							onNavigate={(idx) => void navigateVersion(idx)}
							isDisabled={isStreaming}
						/>
					)}
				</div>
			)}

			<div className={styles.body}>
				{showPreview ? (
					IFRAME_LANGS.has(language.toLowerCase()) ? (
						<iframe
							className={styles.iframe}
							title={panel.title}
							sandbox="allow-scripts"
							srcDoc={content}
						/>
					) : (
						<div className={styles.markdown}>
							<Markdown remarkPlugins={REMARK_PLUGINS}>{stripHtmlComments(content)}</Markdown>
						</div>
					)
				) : (
					<Highlight code={content} language={language || "text"} theme={prismTheme}>
						{({ className, style, tokens, getLineProps, getTokenProps }) => (
							<pre
								className={`${styles.pre} ${className}`}
								style={{ ...style, background: "transparent" }}
							>
								{tokens.map((line, i) => (
									<div key={i} {...getLineProps({ line })}>
										{line.map((token, key) => (
											<span key={key} {...getTokenProps({ token })} />
										))}
									</div>
								))}
							</pre>
						)}
					</Highlight>
				)}
			</div>
		</div>
	);
}
